import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, XCircle, ExternalLink, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const PLATFORM_LABEL = {
  facebook: "Facebook Page",
  instagram: "Instagram Business",
  threads: "Threads",
  x: "X (Twitter)",
  tiktok: "TikTok",
  youtube: "YouTube",
};

const PLATFORM_COLOR = {
  facebook: "#1877F2",
  instagram: "#E1306C",
  threads: "#F5F1EA",
  x: "#0EA5E9",
  tiktok: "#FF0050",
  youtube: "#FF0000",
};

const fmtTs = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function SocialAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("test"); // "test" = probes only; "all" = probes + production

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "test") {
        const r = await api.get(`/social/test-posts/recent?days=${days}&limit=200`);
        setData({ ...r.data, kind: "test" });
      } else {
        const r = await api.get(`/social/publishing-log/recent?days=${days}&limit=200&kind=all`);
        setData({ items: r.data.items, total: r.data.total, days, kind: "all", health: {} });
      }
    } catch (e) {
      toast.error("Could not load audit log");
    } finally { setLoading(false); }
  }, [days, tab]);

  useEffect(() => { load(); }, [load]);

  const health = data?.health || {};
  const items = data?.items || [];
  const totalOk = Object.values(health).reduce((a, h) => a + (h.ok || 0), 0);
  const totalFail = Object.values(health).reduce((a, h) => a + (h.fail || 0), 0);
  const totalAll = totalOk + totalFail;
  const successRate = totalAll === 0 ? null : Math.round((totalOk / totalAll) * 100);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="social-audit-page">
      <div className="mb-4">
        <Link to="/dashboard/social-connections" data-testid="audit-back-link" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Social Connections
        </Link>
      </div>

      <PageHeader
        eyebrow="// SOCIAL AUDIT"
        title="Test Post audit log."
        description="Every Quick Test Post attempt for this station, with success/failure status, external IDs, and per-platform health."
      />

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="inline-flex rounded-md border border-line bg-bg-surface p-1 mr-2" data-testid="audit-tab-switch">
          {[["test", "Test Probes"], ["all", "All Posts (incl. production)"]].map(([t, lbl]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`audit-tab-${t}`}
              className={`text-xs px-3 py-1.5 rounded transition ${tab === t ? "bg-brand-blue/15 text-brand-blue" : "text-ink-muted hover:text-ink-secondary"}`}
            >{lbl}</button>
          ))}
        </div>
        <div className="text-xs text-ink-muted">Window:</div>
        {[7, 30, 90, 180].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`audit-window-${d}`}
            className={`text-xs px-3 py-1.5 rounded-md border transition ${days === d ? "border-brand-amber bg-brand-amber/10 text-brand-amber" : "border-line bg-bg-surface text-ink-secondary hover:border-brand-amber/40"}`}
          >{d} days</button>
        ))}
        <button
          onClick={load}
          disabled={loading}
          data-testid="audit-refresh"
          className="ml-auto text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-line bg-bg-surface hover:border-brand-blue transition disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="audit-summary-total">
          <div className="text-[11px] uppercase tracking-wider text-ink-muted">Total Attempts</div>
          <div className="font-display text-3xl font-bold mt-1">{totalAll}</div>
        </div>
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="audit-summary-ok">
          <div className="text-[11px] uppercase tracking-wider text-emerald-300">Succeeded</div>
          <div className="font-display text-3xl font-bold mt-1 text-emerald-200">{totalOk}</div>
        </div>
        <div className="rounded-xl border border-brand-onair/30 bg-brand-onair/5 p-4" data-testid="audit-summary-fail">
          <div className="text-[11px] uppercase tracking-wider text-brand-onair">Failed</div>
          <div className="font-display text-3xl font-bold mt-1 text-brand-onair">{totalFail}</div>
        </div>
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="audit-summary-rate">
          <div className="text-[11px] uppercase tracking-wider text-ink-muted">Success rate</div>
          <div className="font-display text-3xl font-bold mt-1">
            {successRate === null ? "—" : `${successRate}%`}
          </div>
        </div>
      </div>

      {/* Per-platform health */}
      {Object.keys(health).length > 0 && (
        <div className="rounded-xl border border-line bg-bg-surface p-5 mb-6" data-testid="audit-health-grid">
          <div className="font-display text-lg font-bold mb-3">Per-platform health (last {days} days)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(health).map(([p, h]) => {
              const total = (h.ok || 0) + (h.fail || 0);
              const rate = total ? Math.round((h.ok / total) * 100) : 0;
              const color = PLATFORM_COLOR[p] || "#888";
              return (
                <div key={p} data-testid={`audit-health-${p}`} className="rounded-lg border border-line bg-bg-elevated p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-display font-bold" style={{ color }}>{PLATFORM_LABEL[p] || p}</div>
                    <div className={`text-xs font-mono ${rate >= 90 ? "text-emerald-300" : rate >= 50 ? "text-brand-amber" : "text-brand-onair"}`}>{rate}%</div>
                  </div>
                  <div className="text-[11px] text-ink-muted">{h.ok || 0} ok · {h.fail || 0} fail · {total} total</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Log table */}
      <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="audit-log-table">
        <div className="px-5 py-3 border-b border-line font-display font-bold flex items-center justify-between">
          <span>Recent attempts</span>
          <span className="text-xs font-mono text-ink-muted">{items.length} shown · {data?.total || 0} total</span>
        </div>
        {loading ? (
          <div className="px-5 py-12 text-center font-mono text-xs text-ink-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-ink-muted" data-testid="audit-empty">
            No test posts in this window yet. Click <span className="font-mono text-brand-blue">Test Post</span> on any connected platform card to create one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bg-elevated text-[11px] uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="text-left px-5 py-2 font-mono font-normal">When</th>
                <th className="text-left px-5 py-2 font-mono font-normal">Platform</th>
                <th className="text-left px-5 py-2 font-mono font-normal">Result</th>
                <th className="text-left px-5 py-2 font-mono font-normal">Detail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} data-testid={`audit-row-${i}`} className={`border-t border-line ${it.ok ? "" : "bg-brand-onair/5"}`}>
                  <td className="px-5 py-3 font-mono text-xs text-ink-secondary whitespace-nowrap">{fmtTs(it.when || it.attempted_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold" style={{ color: PLATFORM_COLOR[it.platform] || "#888" }}>
                        {PLATFORM_LABEL[it.platform] || it.platform}
                      </span>
                      {it.kind === "production" && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300">prod</span>
                      )}
                      {it.kind === "test" && tab === "all" && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-bg-elevated text-ink-muted">probe</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {it.ok ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid="audit-row-ok">
                        <CheckCircle2 className="h-3.5 w-3.5" /> ok
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-brand-onair" data-testid="audit-row-fail">
                        <XCircle className="h-3.5 w-3.5" /> failed
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {it.ok ? (
                      it.external_url ? (
                        <a href={it.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-blue hover:underline">
                          View post <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : it.external_id ? (
                        <span className="font-mono text-ink-muted">{it.external_id}</span>
                      ) : <span className="text-ink-muted">—</span>
                    ) : (
                      <span className="text-brand-onair/80 break-all">{it.error || "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
